import { createORPCClient, onError, onSuccess } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { BatchLinkPlugin, SimpleCsrfProtectionLinkPlugin } from "@orpc/client/plugins";
import { StandardRPCJsonSerializer } from "@orpc/client/standard";
import type { ContractRouterClient } from "@orpc/contract";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { AdminAPIContract } from "@workspace/admin-domain/src";
import posthog from "posthog-js";
import { getNestedObjValue, isDefinedError } from "..";
import { ADMIN_ENV } from "../env";

const serializer = new StandardRPCJsonSerializer();
let d1Bookmark = globalThis.localStorage?.getItem("d1-bookmark") ?? null;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      experimental_prefetchInRender: true,
      retry(failureCount, error) {
        if (isDefinedError(error) && error.code === "INTERNAL_SERVER_ERROR") {
          return failureCount < 3;
        }
        return false;
      },
      queryKeyHashFn(queryKey) {
        const [json, meta] = serializer.serialize(queryKey);
        return JSON.stringify({ json, meta });
      },
      staleTime: 60 * 1000,
    },
    dehydrate: {
      serializeData(data) {
        const [json, meta] = serializer.serialize(data);
        return { json, meta };
      },
    },
    hydrate: {
      deserializeData(data) {
        return serializer.deserialize(data.json, data.meta);
      },
    },
  },
});

const link = new RPCLink({
  url: ADMIN_ENV.VITE_PUBLIC_API_URL + "/rpc/admin",
  plugins: [
    new SimpleCsrfProtectionLinkPlugin(),
    new BatchLinkPlugin({ groups: [{ condition: () => true, context: {} }] }),
  ],
  async fetch(request, init) {
    const headers = new Headers(request instanceof Request ? request.headers : undefined);
    if (d1Bookmark) headers.set("X-D1-Bookmark", d1Bookmark);
    const response = await fetch(request, {
      ...init,
      headers,
      credentials: "include",
    });
    d1Bookmark = response.headers.get("X-D1-Bookmark") ?? d1Bookmark;
    if (d1Bookmark) globalThis.localStorage?.setItem("d1-bookmark", d1Bookmark);
    return response;
  },
  interceptors: [
    onError(async (error) => {
      if (isDefinedError(error) && error.code === "UNAUTHORIZED") {
        throw redirect({ to: "/login" });
      }
    }),
    onSuccess(async (output, { path, input }) => {
      const contract: AdminAPIContract["trip"]["get"] | undefined = getNestedObjValue(AdminAPIContract, [...path]);
      if (contract?.["~orpc"].meta.aragon) {
        posthog.capture(contract["~orpc"].meta.aragon, {
          input,
          output,
        });
      }
    }),
  ],
});

const client: ContractRouterClient<AdminAPIContract> = createORPCClient(link);
export const orpc = createTanstackQueryUtils(client);
